/**
 * OverviewDashboard component for stats overview.
 */

import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { SimpleBarChart } from '@/components/shared/Charts/SimpleBarChart'
import { Users, Bot, Coins, DollarSign } from 'lucide-react'
import { useStatsOverview } from '../hooks/use-stats'
import type { Scope } from '../types'

interface OverviewDashboardProps {
  scope: Scope
  tenantId: string | null
  organizationId: string | null
  start: string
  end: string
}

export function OverviewDashboard({
  scope,
  tenantId,
  organizationId,
  start,
  end
}: OverviewDashboardProps) {
  // Check if required parameters are present based on scope
  const hasRequiredParams =
    scope === 'system' ? true :
    scope === 'tenant' ? !!tenantId :
    scope === 'organization' ? !!(tenantId && organizationId) :
    false

  const { data, isLoading, error } = useStatsOverview({
    scope,
    tenantId,
    organizationId,
    start,
    end
  })

  // Show message if required parameters are missing
  if (!hasRequiredParams) {
    return (
      <Card>
        <CardContent className="pt-6">
          <div className="text-center py-12 text-muted-foreground">
            {scope === 'tenant' && !tenantId && 'Please select a tenant to view stats'}
            {scope === 'organization' && !tenantId && 'Please select a tenant first'}
            {scope === 'organization' && tenantId && !organizationId && 'Please select an organization to view stats'}
          </div>
        </CardContent>
      </Card>
    )
  }

  if (error) {
    return (
      <Card>
        <CardContent className="pt-6">
          <div className="text-center text-destructive">
            Error loading stats: {error instanceof Error ? error.message : 'Unknown error'}
          </div>
        </CardContent>
      </Card>
    )
  }

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map((i) => (
            <Card key={i}>
              <CardHeader>
                <div className="h-4 bg-muted animate-pulse rounded" />
              </CardHeader>
              <CardContent>
                <div className="h-8 bg-muted animate-pulse rounded" />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    )
  }

  const summary = data?.summary
  const breakdown = data?.breakdown || []
  const topDomains = data?.topDomains || []

  const breakdownTitle =
    scope === 'system' ? 'Top 10 Tenants' :
    scope === 'tenant' ? 'Organizations' :
    'Domains'

  // Debug logging
  console.log('OverviewDashboard data:', {
    breakdown: breakdown.length,
    breakdownItems: breakdown.map(b => ({ name: b.name, users: b.users, agents: b.agents })),
    topDomains: topDomains.length,
    topDomainsItems: topDomains.map(d => ({ domain: d.domain, users: d.users, agents: d.agents }))
  })

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Users</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{summary?.totalUsers.toLocaleString() || 0}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Agents</CardTitle>
            <Bot className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{summary?.totalAgents.toLocaleString() || 0}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Tokens</CardTitle>
            <Coins className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{summary?.totalTokens.toLocaleString() || 0}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Cost</CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">${summary?.totalCost.toFixed(2) || '0.00'}</div>
          </CardContent>
        </Card>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Breakdown Chart */}
        <SimpleBarChart
          title={`${breakdownTitle} by Users`}
          data={breakdown.map(item => ({
            name: item.name,
            users: item.users,
            agents: item.agents
          }))}
          height={350}
        />

        {/* Top Domains Chart */}
        <SimpleBarChart
          title="Top Domains by Users"
          data={topDomains.map(d => ({
            name: d.domain,
            users: d.users,
            agents: d.agents
          }))}
          height={350}
        />
      </div>

      {/* Breakdown Table */}
      <Card>
        <CardHeader>
          <CardTitle>{breakdownTitle} Breakdown</CardTitle>
          <CardDescription>
            Detailed metrics by {scope === 'system' ? 'tenant' : scope === 'tenant' ? 'organization' : 'domain'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b">
                  <th className="text-left p-2">Name</th>
                  <th className="text-right p-2">Users</th>
                  <th className="text-right p-2">Agents</th>
                  <th className="text-right p-2">Tokens</th>
                  <th className="text-right p-2">Cost</th>
                </tr>
              </thead>
              <tbody>
                {breakdown.map((item) => (
                  <tr key={item.id} className="border-b hover:bg-muted/50">
                    <td className="p-2 font-medium">{item.name}</td>
                    <td className="text-right p-2">{item.users.toLocaleString()}</td>
                    <td className="text-right p-2">{item.agents.toLocaleString()}</td>
                    <td className="text-right p-2">{item.tokens.toLocaleString()}</td>
                    <td className="text-right p-2">${item.cost.toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {breakdown.length === 0 && (
              <div className="text-center py-8 text-muted-foreground">
                No data available for the selected scope
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
